// Contas a pagar e a receber (0.28): a conta aberta com o boleto, o código para pagar, os comprovantes de cada
// vencimento e a tela de cadastros (tudo o que foi cadastrado, mais o arquivo de comprovantes).
import { useState } from 'react';
import { ActivityIndicator, Alert, Pressable, ScrollView, Share, Text, View } from 'react-native';
import type { ContaFin, Financas } from '../../store/types';
import { alternarPago, arquivoDeComprovantes, cartoesDe, definirValorPago, diferencaPaga, excluirConta, metasDe, salvarAnexosConta, salvarComprovantes, valorPagoDe } from '../../store/financas';
import { AnexosEditor, abrirQualquer, iconeAnexo } from '../Anexos';
import { nomeAnexo } from '../../lib/nomeArquivo';
import { Btn, Chip, Field, Sheet } from '../ui';
import { C, alpha } from '../theme';
import { brYmd } from '../../lib/date';
import { formatarCodigo, lerCodigo } from '../../lib/boleto';
import { semBloqueio } from '../../auth/AuthContext';
import { t } from '../../lib/i18n';
import { lerValor } from '../../lib/moeda';
import { paraLixeira } from '../../lib/lixeira';
import { getDoc } from '../../store/store';
import { useAcoes } from '../Acoes';
import { useFonteIA } from '../FonteIA';
import { useIAPronta } from '../../lib/ia';
import { lerComprovante } from '../../lib/iaUsos';
import { lerBase64 } from '../../lib/anexos';
import { useCaixas } from '../../lib/caixas';
import { ROLAGEM_TECLADO } from '../teclado';

export type AlvoConta = { id: string; venc: string };

/** Exclui a conta guardando na lixeira (30 dias) a conta e os pagamentos lançados dela. */
export function apagarConta(c: ContaFin) {
  paraLixeira('financas', 'financas', c.nome, { contas: [c], lancamentos: getDoc('financas').lancamentos.filter(l => l.id.startsWith(`c:${c.id}:`)) });
  excluirConta(c.id);
}

/** Uma conta aberta: valor, vencimento, código para pagar, boleto e documentos, e o comprovante deste vencimento. */
export function ContaDetalhe({ d, alvo, onClose, cor, $, editar, lembrete, excluir }:
  { d: Financas; alvo: AlvoConta | null; onClose: () => void; cor: string; $: (v: number) => string;
    editar: (c: ContaFin) => void; lembrete: (c: ContaFin, venc: string) => void; excluir?: (c: ContaFin) => void }) {
  const c = alvo ? d.contas.find(x => x.id === alvo.id) ?? null : null;
  const [valorTxt, setValorTxt] = useState<string | null>(null);   // editando o valor pago
  const { confirmar } = useAcoes();
  const fonte = useFonteIA();
  const temIA = useIAPronta();
  const [lendo, setLendo] = useState(false);
  if (!c || !alvo) return <Sheet visible={false} onClose={onClose} title=""><View /></Sheet>;
  const venc = alvo.venc, pago = c.pagos.includes(venc), pagar = c.tipo === 'pagar';
  const lido = c.codigo ? lerCodigo(c.codigo) : null;
  const comps = c.comprovantes?.[venc] ?? [];
  const anteriores = Object.entries(c.comprovantes ?? {}).filter(([v, l]) => v !== venc && l.length).sort((a, b) => b[0].localeCompare(a[0]));
  const depois = (f: () => void) => { onClose(); setTimeout(f, 400); };
  /** 0.65 (B16): guarda a foto do comprovante deste vencimento e, com a IA ligada, lê o valor pago e a data. */
  async function comprovantePelaIA() {
    if (!c) return;
    const r = await fonte.escolher(false);
    if (!r) return;
    salvarComprovantes(c.id, venc, [...comps, r.arq]);
    if (!temIA) return Alert.alert(t('Comprovante guardado'), pago ? t('Com a sua IA ligada (Ajustes › Inteligência artificial), o app lê o valor, a data e a descrição pela foto.')
      : (pagar ? t('Marcar esta conta como paga?') : t('Marcar como recebida?')), pago ? undefined : [
      { text: t('Agora não'), style: 'cancel' }, { text: t('Sim'), onPress: () => alternarPago(c, venc) }]);
    setLendo(true);
    try {
      const x = await lerComprovante({ mime: r.arq.mime || 'image/jpeg', base64: await lerBase64(r.arq) }, [], !pagar);
      const v = x.valor;
      const msg = [v ? (pagar ? t('Valor pago: {v}', { v: $(v) }) : t('Valor recebido: {v}', { v: $(v) })) : t('A IA não achou o valor no comprovante.'), x.data ? brYmd(x.data) : ''].filter(Boolean).join(' · ');
      const igual = v != null && Math.abs(v - valorPagoDe(c, venc)) < 0.005;
      if (pago) return Alert.alert(t('Comprovante guardado'), msg, v && !igual ? [{ text: t('Agora não'), style: 'cancel' }, { text: t('Usar este valor'), onPress: () => definirValorPago(c, venc, v) }] : undefined);
      Alert.alert(t('Comprovante guardado'), msg + '\n\n' + (pagar ? t('Marcar esta conta como paga?') : t('Marcar como recebida?')), [
        { text: t('Agora não'), style: 'cancel' }, { text: t('Sim'), onPress: () => (v ? alternarPago(c, venc, v) : alternarPago(c, venc)) }]);
    } catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
    finally { setLendo(false); }
  }
  return (
    <Sheet visible onClose={onClose} title={`${c.icone} ${c.nome}`}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 80 }}>
        <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 6 }}>
          <Text style={{ color: pagar ? C.text : C.ok, fontSize: 26, fontWeight: '800' }}>{pagar ? '' : '+'}{$(c.valor)}</Text>
          <Text style={{ color: C.sub }}>{t('Vence {data}', { data: brYmd(venc) })}{c.repete === 'mensal' ? ' · 🔁 ' + t('Todo mês') : c.repete === 'anual' ? ' · 🔁 ' + t('Todo ano') : ''}</Text>
          <Pressable onPress={() => { setValorTxt(null); alternarPago(c, venc); }}
            style={{ alignSelf: 'flex-start', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: pago ? alpha(C.ok, 0.18) : alpha(cor, 0.18) }}>
            <Text style={{ color: pago ? C.ok : cor, fontWeight: '700' }}>{pago ? '✓ ' + (pagar ? t('Paga (toque para desfazer)') : t('Recebida (toque para desfazer)'))
              : (pagar ? t('Marcar como paga') : t('Marcar como recebida'))}</Text>
          </Pressable>
          {pago && (() => {
            const dif = diferencaPaga(c, venc);
            return (<>
              <Text style={{ color: C.text, fontWeight: '700' }}>{pagar ? t('Valor pago: {v}', { v: $(valorPagoDe(c, venc)) }) : t('Valor recebido: {v}', { v: $(valorPagoDe(c, venc)) })}</Text>
              {dif !== 0 && <Text style={{ color: dif < 0 ? C.ok : C.danger, fontSize: 13 }}>{dif < 0 ? '🏷️ ' + t('Desconto de {v}', { v: $(-dif) }) : '⚠️ ' + t('Multa e juros de {v}', { v: $(dif) })}</Text>}
            </>);
          })()}
          {valorTxt === null ? (
            <Pressable onPress={() => setValorTxt(String(valorPagoDe(c, venc)).replace('.', ','))} hitSlop={6}>
              <Text style={{ color: cor, fontSize: 13, fontWeight: '700' }}>{pago ? '✏️ ' + t('Alterar o valor pago') : '💲 ' + (pagar ? t('Paguei outro valor (desconto, multa ou juros)') : t('Recebi outro valor'))}</Text>
            </Pressable>
          ) : (
            <View style={{ gap: 8 }}>
              <Field value={valorTxt} onChangeText={setValorTxt} keyboardType="decimal-pad" autoFocus placeholder={pagar ? t('Valor pago') : t('Valor recebido')} />
              {(() => { const v = lerValor(valorTxt), dif = Math.round((v - c.valor) * 100) / 100;
                return v > 0 && dif !== 0 ? <Text style={{ color: dif < 0 ? C.ok : C.danger, fontSize: 12 }}>{dif < 0 ? t('Desconto de {v}', { v: $(-dif) }) : t('Multa e juros de {v}', { v: $(dif) })}</Text> : null; })()}
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View style={{ flex: 1 }}><Btn outline cor={cor} label={t('Cancelar')} onPress={() => setValorTxt(null)} /></View>
                <View style={{ flex: 1 }}><Btn cor={cor} label={pago ? t('Salvar') : (pagar ? t('Marcar como paga') : t('Marcar como recebida'))} onPress={() => {
                  const v = lerValor(valorTxt);
                  if (!(v > 0)) return Alert.alert(t('Informe o valor.'));
                  if (pago) definirValorPago(c, venc, v); else alternarPago(c, venc, v);
                  setValorTxt(null);
                }} /></View>
              </View>
            </View>
          )}
        </View>

        {!!c.codigo && (
          <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 8 }}>
            <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }}>{lido?.tipo === 'pix' ? t('PIX COPIA E COLA') : t('CÓDIGO PARA PAGAR')}</Text>
            <Text selectable style={{ color: C.text, fontFamily: 'monospace', fontSize: 13 }}>{formatarCodigo(c.codigo)}</Text>
            <Text style={{ color: C.mute, fontSize: 11 }}>{t('Pressione e segure o código para copiar, ou toque em Enviar e escolha "Copiar".')}</Text>
            <Btn outline cor={cor} label={'📤 ' + t('Enviar ou copiar o código')} onPress={() => Share.share({ message: c.codigo! }).catch(() => {})} />
          </View>
        )}

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }}>{pagar ? t('COMPROVANTE DE PAGAMENTO') : t('COMPROVANTE DE RECEBIMENTO')} · {brYmd(venc)}</Text>
        <Pressable onPress={comprovantePelaIA} disabled={lendo} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, backgroundColor: alpha(cor, 0.15) }}>
          {lendo ? <ActivityIndicator color={cor} /> : <Text style={{ fontSize: 18 }}>🤖</Text>}
          <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>{lendo ? t('Lendo o comprovante...') : t('Ler o comprovante com a minha IA')}</Text>
        </Pressable>
        <AnexosEditor lista={comps} cor={cor} aviso={false} onChange={l => {
          salvarComprovantes(c.id, venc, l);
          if (l.length > comps.length && !pago) Alert.alert(t('Comprovante guardado'), pagar ? t('Marcar esta conta como paga?') : t('Marcar como recebida?'), [
            { text: t('Agora não'), style: 'cancel' }, { text: t('Sim'), onPress: () => alternarPago(c, venc) }]);
        }} />

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('BOLETO E DOCUMENTOS')}</Text>
        <AnexosEditor lista={c.anexos ?? []} cor={cor} aviso={false} onChange={l => salvarAnexosConta(c.id, l)} />

        {anteriores.length > 0 && (<>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('COMPROVANTES ANTERIORES')}</Text>
          {anteriores.map(([v, l]) => (
            <View key={v} style={{ backgroundColor: C.card, borderRadius: 12, padding: 10, gap: 4 }}>
              <Text style={{ color: C.text, fontWeight: '700' }}>{brYmd(v)}</Text>
              {l.map((a, i) => <Pressable key={i} onPress={() => semBloqueio(() => abrirQualquer(a)).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e)))}>
                <Text style={{ color: cor }} numberOfLines={1}>{iconeAnexo(a)} {nomeAnexo(a)}</Text></Pressable>)}
            </View>
          ))}
        </>)}

        {!!c.notas && <Text style={{ color: C.sub }}>{c.notas}</Text>}
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <View style={{ flex: 1 }}><Btn outline cor={cor} label={'📅 ' + t('Lembrete')} onPress={() => depois(() => lembrete(c, venc))} /></View>
          <View style={{ flex: 1 }}><Btn outline cor={cor} label={'✏️ ' + t('Editar')} onPress={() => depois(() => editar(c))} /></View>
        </View>
        <Pressable onPress={() => Alert.alert(t('Excluir "{nome}"?', { nome: c.nome }), [c.repete !== 'nao' ? t('Apaga todos os meses desta conta.') : '', t('Fica 30 dias na lixeira.')].filter(Boolean).join('\n\n'), [
          { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => { onClose(); setTimeout(async () => {
            if (await confirmar(t('Confirme para excluir'))) (excluir ?? apagarConta)(c); }, 400); } }])}
          style={{ alignItems: 'center', padding: 10 }}><Text style={{ color: C.danger }}>{t('Excluir conta')}</Text></Pressable>
      </ScrollView>
      {fonte.tela}
    </Sheet>
  );
}

type Aba = 'pagar' | 'receber' | 'cartoes' | 'metas' | 'comprovantes';
/** Tudo o que está cadastrado nas Finanças, num lugar só, e o arquivo de comprovantes. */
export function CadastrosSheet({ d, aberta, fechar, cor, $, abrirConta, novaConta, abrirCartao, novoCartao, editarMeta, novaMeta }:
  { d: Financas; aberta: boolean; fechar: () => void; cor: string; $: (v: number) => string;
    abrirConta: (c: ContaFin) => void; novaConta: (tipo: 'pagar' | 'receber') => void; abrirCartao: (id: string) => void; novoCartao: () => void;
    editarMeta: (id: string) => void; novaMeta: () => void }) {
  const [aba, setAba] = useState<Aba>('pagar');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const ir = (f: () => void) => { fechar(); setTimeout(f, 400); };
  const contas = d.contas.filter(c => c.tipo === (aba === 'receber' ? 'receber' : 'pagar')).sort((a, b) => a.nome.localeCompare(b.nome));
  const arquivo = arquivoDeComprovantes(d);
  const linha = (key: string, icone: string, titulo: string, sub: string, valor: string, onPress: () => void) => (
    <Pressable key={key} onPress={onPress} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, ...cx.pads(12), borderRadius: cx.raio(14), backgroundColor: C.card }}>
      <Text style={{ fontSize: cx.icone(22) }}>{icone}</Text>
      <View style={{ flex: 1 }}>
        <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={1}>{titulo}</Text>
        {!!sub && <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{sub}</Text>}
      </View>
      <Text style={{ color: C.text, fontWeight: '700' }}>{valor}</Text>
    </Pressable>
  );
  const repete = (c: ContaFin) => (c.repete === 'mensal' ? t('todo dia {d}', { d: Number(c.venc.slice(8)) }) : c.repete === 'anual' ? t('todo ano em {d}', { d: brYmd(c.venc).slice(0, 5) }) : t('uma vez em {d}', { d: brYmd(c.venc) }));
  return (
    <Sheet visible={aberta} onClose={fechar} title={t('Cadastros')}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6, paddingHorizontal: 16, paddingBottom: 8 }} style={{ flexGrow: 0 }}>
        {([['pagar', '🧾 ' + t('Contas a pagar')], ['receber', '💰 ' + t('Contas a receber')], ['cartoes', '💳 ' + t('Cartões')], ['metas', '🎯 ' + t('Metas')],
          ['comprovantes', '📎 ' + t('Comprovantes')]] as [Aba, string][]).map(([k, l]) => <Chip key={k} cor={cor} on={aba === k} label={l} onPress={() => setAba(k)} />)}
      </ScrollView>
      <ScrollView contentContainerStyle={{ padding: 16, gap: cx.gap(8), paddingBottom: 80 }}>
        {(aba === 'pagar' || aba === 'receber') && (<>
          <Btn cor={cor} label={'+ ' + (aba === 'pagar' ? t('Conta a pagar') : t('Conta a receber'))} onPress={() => ir(() => novaConta(aba))} />
          {!contas.length && <Text style={{ color: C.sub }}>{aba === 'pagar' ? t('Nenhuma conta a pagar cadastrada.') : t('Nenhuma conta a receber cadastrada.')}</Text>}
          {contas.map(c => linha(c.id, c.icone, c.nome, [repete(c), c.fim ? t('até {d}', { d: brYmd(c.fim) }) : '', c.codigo ? '🔢' : '', (c.anexos ?? []).length ? '📎' : ''].filter(Boolean).join(' · '),
            $(c.valor), () => ir(() => abrirConta(c))))}
        </>)}
        {aba === 'cartoes' && (<>
          <Btn cor={cor} label={'+ ' + t('Novo cartão de crédito')} onPress={() => ir(novoCartao)} />
          {cartoesDe(d).map(c => linha(c.id, c.icone, c.nome, t('Fecha dia {f} · vence dia {v}', { f: c.fecha, v: c.vence }), c.limite ? $(c.limite) : '', () => ir(() => abrirCartao(c.id))))}
        </>)}
        {aba === 'metas' && (<>
          <Btn cor={cor} label={'+ ' + t('Nova meta')} onPress={() => ir(novaMeta)} />
          {metasDe(d).map(m => linha(m.id, m.icone, m.nome, m.prazo ? t('Prazo: {d}', { d: brYmd(m.prazo) }) : t('Sem prazo'), $(m.alvo), () => ir(() => editarMeta(m.id))))}
        </>)}
        {aba === 'comprovantes' && (<>
          {!arquivo.length && <Text style={{ color: C.sub }}>{t('Nenhum comprovante guardado ainda. Ao pagar uma conta, toque nela e anexe a foto, o PDF ou o print do comprovante.')}</Text>}
          {arquivo.map(x => (
            <View key={x.conta.id + x.venc} style={{ backgroundColor: C.card, borderRadius: cx.raio(12), padding: cx.pad(10), gap: cx.gap(4) }}>
              <Text style={{ color: C.text, fontWeight: '700' }}>{x.conta.icone} {x.conta.nome} · {brYmd(x.venc)}</Text>
              {x.anexos.map((a, i) => <Pressable key={i} onPress={() => semBloqueio(() => abrirQualquer(a)).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e)))}>
                <Text style={{ color: cor }} numberOfLines={1}>{iconeAnexo(a)} {nomeAnexo(a)}</Text></Pressable>)}
            </View>
          ))}
        </>)}
      </ScrollView>
    </Sheet>
  );
}
